import React, {useEffect, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity, TouchableWithoutFeedback, View} from "react-native";
import {useDispatch, useSelector} from "react-redux";
import {Icon} from "react-native-elements";
import {formatSharpHour, formatTimeFromMoment, getClosestMultipleOfGranularity, getDaySchedule} from "../services/helpers/dateTimeHelpers";
import {
    getBookingList,
    getBookModalVisibility,
    getComponentColor,
    getConfig,
    getLoading,
    setSelectedInterval
} from "../reducers/configSlice";
import moment from "moment-timezone";
import moment_tz from "moment-timezone";
import {GestureHandlerRootView, ScrollView} from "react-native-gesture-handler";
import DateTimePickerModal from "react-native-modal-datetime-picker";
import {FONT_FAMILY, FONT_FAMILY_BOLDER, FONT_FAMILY_LIGHT} from "../constants/styleConstants";

import {actuatedNormalize} from "../services/helpers/stylingHelpers";
import {isIntervalDisabled} from "../services/helpers/bookingsHelpers";

export const BookingIntervalContainer = ({setBlurEffect, date}) => {
    const dispatch = useDispatch()
    const loading = useSelector(getLoading)
    const componentColor = useSelector(getComponentColor)
    const config = useSelector(getConfig)
    const bookModalVisible = useSelector(getBookModalVisibility)
    const bookingList = useSelector(getBookingList)

    const [selectorVisible, setSelectorVisible] = useState(false)
    const [daySchedule, setDaySchedule] = useState(getDaySchedule(date))
    const [customSelector, setCustomSelector] = useState(false)

    const [customInfo, setCustomInfo] = useState(null)
    const [customStart, setCustomStart] = useState(getClosestMultipleOfGranularity(config.granularity))
    const [customEnd, setCustomEnd] = useState(moment_tz.tz(date + " " + getClosestMultipleOfGranularity(config.granularity), config.timezone).utc().tz(config.timezone).add(config.default_booking_duration, 'minutes').format('HH:mm'))
    const [minStartTime, setMinStartTime] = useState(formatSharpHour(daySchedule.start))
    const [maxEndTime, setMaxEndTime] = useState(formatSharpHour(daySchedule.end))
    const [chosenSlot, setChosenSlot] = useState(null)
    const [freeIntervals, setFreeIntervals] = useState([])

    const getFreeIntervals = (list) => {
        if (!list) {
            list = bookingList
        }

        let freeIntervals = []
        const schedule = getDaySchedule(date)
        const scheduleStartMoment = moment_tz.tz(date + " " + formatSharpHour(schedule.start), config.timezone).utc().tz(config.timezone)
        const scheduleEndMoment = moment_tz.tz(date + " " + formatSharpHour(schedule.end), config.timezone).utc().tz(config.timezone)

        if (schedule.blocked) {
            return freeIntervals
        }

        if (list.length < 1) {
            freeIntervals.push({
                start: formatSharpHour(schedule.start),
                end: formatSharpHour(schedule.end),
                current: true
            })

            return freeIntervals
        }

        const now = moment_tz.tz(moment().tz(config.timezone).format('YYYY-MM-DD HH:mm'), config.timezone).utc().tz(config.timezone)

        list.filter( (booking, index) => {
            if (!list[index - 1]) {
                const bookingStartMoment = moment_tz.tz(booking.start, config.timezone).utc().tz(config.timezone)

                if (bookingStartMoment.format('HH:mm') !== formatSharpHour(daySchedule.start)) {
                    freeIntervals.push({
                        start: formatSharpHour(daySchedule.start),
                        end: bookingStartMoment.format('HH:mm'),
                        current: now.isBetween(scheduleStartMoment, bookingStartMoment),
                        inFuture: scheduleStartMoment.isAfter(now)
                    })
                }
            }

            if (list[index - 1]) {
                const bookingStartMoment = moment_tz.tz(booking.start, config.timezone).utc().tz(config.timezone)
                const prevBookingEndMoment = moment_tz.tz(list[index - 1].end, config.timezone).utc().tz(config.timezone)

                if (bookingStartMoment.format('HH:mm') !== prevBookingEndMoment.format("HH:mm")) {
                    freeIntervals.push({
                        start: prevBookingEndMoment.format('HH:mm'),
                        end: bookingStartMoment.format('HH:mm'),
                        current: now.isBetween(prevBookingEndMoment, bookingStartMoment),
                        inFuture: prevBookingEndMoment.isAfter(now)
                    })
                }
            }

            if (!list[index + 1]) {
                const bookingEndMoment = moment_tz.tz(booking.end, config.timezone).utc().tz(config.timezone)

                if (bookingEndMoment.format('HH:mm') !== formatSharpHour(daySchedule.end)) {
                    freeIntervals.push({
                        start: bookingEndMoment.format('HH:mm'),
                        end: formatSharpHour(daySchedule.end),
                        current: now.isBetween(bookingEndMoment, scheduleEndMoment),
                        inFuture: bookingEndMoment.isAfter(now)
                    })
                }
            }
        })

        return freeIntervals
    }

    const isDayFullyBooked = (list = null) => {
        if (!list) {
            list = bookingList
        }

        const schedule = getDaySchedule(date)
        if (schedule.blocked) {
            return true
        }

        if (list.length < 1) {
            return false
        }

        if (list.length === 1) {
            const bookingStartMoment = moment_tz.tz(list[0].start, config.timezone).utc().tz(config.timezone)
            const bookingEndMoment = moment_tz.tz(list[0].end, config.timezone).utc().tz(config.timezone)

            if (bookingStartMoment.format('HH:mm') === formatSharpHour(schedule.start) && bookingEndMoment.format("HH:mm") === formatSharpHour(schedule.end)) {
                return true
            }
        }

        let fullyBooked = true;
        let toContinue = true
        let freeIntervals = []
        list.filter( (booking, index) => {
            if (toContinue) {
                if (!list[index - 1]) {
                    const bookingStartMoment = moment_tz.tz(booking.start, config.timezone).utc().tz(config.timezone)

                    if (bookingStartMoment.format('HH:mm') !== formatSharpHour(schedule.start)) {
                        freeIntervals.push({
                            start: formatSharpHour(schedule.start),
                            end: bookingStartMoment.format('HH:mm')
                        })

                        toContinue = false
                        fullyBooked = false
                        return
                    }
                }

                if (list[index - 1]) {
                    const bookingStartMoment = moment_tz.tz(booking.start, config.timezone).utc().tz(config.timezone)
                    const prevBookingEndMoment = moment_tz.tz(list[index - 1].end, config.timezone).utc().tz(config.timezone)

                    if (bookingStartMoment.format('HH:mm') !== prevBookingEndMoment.format("HH:mm")) {
                        freeIntervals.push({
                            start: prevBookingEndMoment.format('HH:mm'),
                            end: bookingStartMoment.format('HH:mm')
                        })

                        toContinue = false
                        fullyBooked = false
                        return
                    }
                }

                if (!list[index + 1]) {
                    const bookingEndMoment = moment_tz.tz(booking.end, config.timezone).utc().tz(config.timezone)

                    if (bookingEndMoment.format('HH:mm') !== formatSharpHour(schedule.end)) {
                        freeIntervals.push({
                            start: bookingEndMoment.format('HH:mm'),
                            end: formatSharpHour(schedule.end)
                        })

                        toContinue = false
                        fullyBooked = false
                    }
                }
            }
        })

        // setFreeIntervals(freeIntervals)
        return fullyBooked
    }

    const getIntervals = () => {
        if (!config.intervals) {
            return []
        }

        let available = []

        if (config.intervals.full_day) {
            available.push({
                name: translate("Full day"),
                start: formatSharpHour(daySchedule.start),
                end: formatSharpHour(daySchedule.end),
                disabled: daySchedule.blocked || isIntervalDisabled(date, formatSharpHour(daySchedule.start), formatSharpHour(daySchedule.end)),
                fullDay: true
            })
        }

        if (config.intervals.intervals) {
            config.intervals.intervals.forEach( (interval) => {
                available.push({...interval, disabled: daySchedule.blocked || isIntervalDisabled(date, interval.start, interval.end)})
            })
        }

        if (config.intervals.custom) {
            available.push({
                name: translate("Other"),
                start: formatSharpHour(daySchedule.start),
                end: formatSharpHour(daySchedule.end),
                custom: true,
                disabled: isDayFullyBooked()
            })
        }

        return available
    }

    useEffect(() => {
        if (!bookModalVisible) {
            setSelectorVisible(false)
            toggleCustomSelector(false)
        }
    }, [bookModalVisible])

    useEffect( () => {
        if (!chosenSlot && intervals && intervals.length) {
            const firstAvailable = intervals.find( interval => {
                return !interval.disabled
            })

            if (firstAvailable) {
                if (firstAvailable.custom) {
                    setSlot({...firstAvailable, start: customStart, end: customEnd})
                } else {
                    setSlot(firstAvailable)
                }
            }
        }
    }, [intervals])

    useEffect( () => {
        if (chosenSlot && chosenSlot.start && chosenSlot.end) {
            dispatch(setSelectedInterval([chosenSlot.start, chosenSlot.end]))
        } else {
            dispatch(setSelectedInterval([]))
        }
    }, [chosenSlot])

    useEffect(() => {
        if (bookingList) {
            const customStartMoment = moment_tz.tz(date + " " + customStart, config.timezone).utc().tz(config.timezone)
            const customEndMoment = moment_tz.tz(date + " " + customEnd, config.timezone).utc().tz(config.timezone)

            const overlappingBooking = bookingList.find( (booking, index) => {
                const startMoment = moment_tz.tz(booking.start, config.timezone).utc().tz(config.timezone)
                const endMoment = moment_tz.tz(booking.end, config.timezone).utc().tz(config.timezone)

                if (customStartMoment.isSameOrAfter(startMoment) && customStartMoment.isBefore(endMoment)) {
                    return booking
                }
            })

            if (overlappingBooking) {
                const overlappingBookingEndMoment = moment_tz.tz(overlappingBooking.end, config.timezone).utc().tz(config.timezone)

                const nextAvailableInterval = freeIntervals.find( interval => {
                    if (interval.inFuture) {
                        return interval
                    }
                })

                if (nextAvailableInterval) {
                    setCustomInfo(translate("%s is unavailable. Next available start time is %s", [formatTimeFromMoment(customStartMoment), nextAvailableInterval.start]))
                } else if (freeIntervals[freeIntervals.length -  1]) {
                    setCustomInfo(translate("There are no intervals available to book after %s", [freeIntervals[freeIntervals.length -  1].start]))
                } else {
                    setCustomInfo(translate("There are no intervals available to book"))
                }
            } else {
                setCustomInfo(null)
            }

            const nextBooking = bookingList.find( (booking, index) => {
                const startMoment = moment_tz.tz(booking.start, config.timezone).utc().tz(config.timezone)

                if (customStartMoment.isBefore(startMoment)) {
                    return booking
                }
            })

            if (nextBooking) {
                const nextBookingStartMoment = moment_tz.tz(nextBooking.start, config.timezone).utc().tz(config.timezone)
                setMaxEndTime(nextBookingStartMoment.format('HH:mm'))

                if (nextBookingStartMoment.isAfter(customEndMoment)) {
                    setCustomEnd(nextBookingStartMoment.format('HH:mm'))
                }
            } else {
                setMaxEndTime(formatSharpHour(daySchedule.end))
            }

            if (customEndMoment.isSameOrBefore(customStartMoment)) {
                setCustomEnd(customStartMoment.add(config.granularity, 'minutes').format("HH:mm"))
            }
        }
    }, [customStart])

    useEffect(() => {
        const customEndMoment = moment_tz.tz(date + " " + customEnd, config.timezone).utc().tz(config.timezone)
        const maxEndTimeMoment = moment_tz.tz(date + " " + maxEndTime, config.timezone).utc().tz(config.timezone)

        if (maxEndTimeMoment.isBefore(customEndMoment)) {
            setCustomEnd(maxEndTimeMoment.format('HH:mm'))
        }
    }, [maxEndTime])

    useEffect(() => {
        if (bookingList) {
            let newIntervals = intervals.map( interval => {
                const intervalStart = interval.fullDay ? formatSharpHour(getDaySchedule(date).start) : interval.start
                const intervalEnd = interval.fullDay ? formatSharpHour(getDaySchedule(date).end) : interval.end

                let newInterval = {...interval,
                    start: intervalStart,
                    end: intervalEnd,
                    disabled: (interval.custom ? isDayFullyBooked(bookingList) : isIntervalDisabled(date, intervalStart, intervalEnd, bookingList)),
                    selected: false
                }

                if (interval.fullDay) {
                    newInterval = {...newInterval, start: formatSharpHour(getDaySchedule(date).start), end: formatSharpHour(getDaySchedule(date).end)}
                }

                return newInterval
            })

            setChosenSlot(null)
            setFreeIntervals(getFreeIntervals(bookingList))

            let newCustomStart
            let newCustomEnd

            const currentFreeInterval = getFreeIntervals(bookingList).find( interval => {
                return interval.current
            })

            if (currentFreeInterval) {
                let customStartMoment = moment_tz.tz(date + " " + customStart, config.timezone).utc().tz(config.timezone)
                let customEndMoment = moment_tz.tz(date + " " + customEnd, config.timezone).utc().tz(config.timezone)
                let currentFreeStartMoment = moment_tz.tz(date + " " + currentFreeInterval.start, config.timezone).utc().tz(config.timezone)
                let currentFreeEndMoment = moment_tz.tz(date + " " + currentFreeInterval.end, config.timezone).utc().tz(config.timezone)

                if (!customStartMoment.isBetween(currentFreeStartMoment, currentFreeEndMoment)) {
                    newCustomStart = currentFreeInterval.start
                } else {
                    newCustomStart = customStart
                }

                if (moment_tz.tz(date + " " + newCustomStart, config.timezone).utc().tz(config.timezone).add(config.default_booking_duration, 'minutes').isBefore(currentFreeEndMoment)) {
                    newCustomEnd = moment_tz.tz(date + " " + newCustomStart, config.timezone).utc().tz(config.timezone).add(config.default_booking_duration, 'minutes').format('HH:mm')
                } else {
                    newCustomEnd = currentFreeInterval.end
                }
            } else {
                const firstFutureFreeInterval = getFreeIntervals(bookingList).find( interval => {
                    return interval.inFuture
                })

                if (firstFutureFreeInterval) {
                    newCustomStart = firstFutureFreeInterval.start
                    newCustomEnd = firstFutureFreeInterval.end
                } else {

                    if (getFreeIntervals(bookingList)[0]) {
                        newCustomStart = getFreeIntervals(bookingList)[0].start
                        newCustomEnd = getFreeIntervals(bookingList)[0].end
                    } else {
                        newCustomStart = formatSharpHour(daySchedule.start)
                        newCustomEnd = formatSharpHour(daySchedule.end)
                    }
                }
            }

            setCustomStart(newCustomStart)
            setCustomEnd(newCustomEnd)

            const firstAvailable = newIntervals.find( interval => {
                return !interval.disabled
            })

            if (firstAvailable) {
                newIntervals = newIntervals.map( (interval, index) => {
                    if (interval.name === firstAvailable.name) {
                        if (firstAvailable.custom) {
                            newCustomEnd = moment_tz.tz(date + " " + newCustomStart, config.timezone).add(config.default_booking_duration, 'minutes').format('HH:mm')

                            setChosenSlot({...firstAvailable, selected: true, start: newCustomStart, end: newCustomEnd})
                            return {...firstAvailable, selected: true, start: newCustomStart, end: newCustomEnd}
                        } else {
                            setChosenSlot({...firstAvailable, selected: true})
                            return {...firstAvailable, selected: true}
                        }
                    }

                    return interval
                })
            }

            setIntervals(newIntervals)
        }
    }, [bookingList])

    useEffect(() => {
        if (date) {
            setDaySchedule(getDaySchedule(date))
        }
    }, [date])

    const [intervals, setIntervals] = useState(getIntervals())

    const toggleSelector = () => {
        setSelectorVisible(!selectorVisible)
    };

    const toggleCustomSelector = (value) => {
        setBlurEffect(value)
        setCustomSelector(value)
    }

    const chooseSlot = (interval) => {
        if (interval.custom) {
            toggleCustomSelector(true)
        } else {
            setSlot(interval)
        }
    }

    const setSlot = (interval) => {
        const newIntervals = intervals.map( (int) => {
            if (int.name === interval.name) {
                return {...int, selected: 1, start: interval.start, end: interval.end}
            }

            return {...int, selected: 0}
        })


        setIntervals(newIntervals)

        setChosenSlot(interval)
    }

    const setCustomSlot = () => {
        setSlot({
            name: translate("Other"),
            start: customStart,
            end: customEnd,
            custom: 1
        })

        toggleCustomSelector(false)
    }

    const getCustomSlot = () => {
        return intervals.find( int => int.custom === 1)
    }

    const renderInterval = (interval, index) => {
        const intervalContainerStyle = () => {
            return {
                borderRightColor:  Color(componentColor).alpha(0.4).rgb().string(),
                backgroundColor: Color(componentColor).alpha(0.7).rotate(2).darken(0.7).rgb().string(),
                borderLeftColor:  Color(componentColor).alpha(0.4).rgb().string(),
            }
        }

        const intervalContainerStyleSelected = () => {
            return {
                backgroundColor: Color(componentColor).alpha(0.4).rgb().string(),
            }
        }

        return (
            <View key={index} style={[styles.intervalContainer, intervalContainerStyle(), interval.selected && intervalContainerStyleSelected(), interval.disabled && {opacity: 0.5}]}>
                <TouchableOpacity disabled={interval.disabled} style={{ justifyContent: 'center', alignItems: 'center'}} onPress={ () => chooseSlot(interval)} hitSlop={{ top: 30, bottom: 30, left: 30, right: 30 }}>
                    <View style={[{ justifyContent: 'center', alignItems: 'center'}]}>
                        {
                            interval.name ?
                                <Text numberOfLines={1} ellipsizeMode='tail' style={[styles.intervalName, interval.start && interval.end && {marginBottom: actuatedNormalize(2.5)}]}>
                                    {interval.name}
                                </Text> : null
                        }
                        {
                            interval.start && interval.end && (!interval.custom || (interval.custom && interval.selected)) ?
                                <Text style={[styles.intervalDuration]}>
                                    {formatTimeFromMoment(moment(interval.start, 'HH:mm'))} - {formatTimeFromMoment(moment(interval.end, 'HH:mm'))}
                                </Text> : null
                        }
                    </View>
                </TouchableOpacity>
            </View>
        )
    }

    return (
        <GestureHandlerRootView style={[
            styles.container,
            selectorVisible && styles.containerWithSelector,
            {
                borderColor: Color(componentColor).alpha(0.4).rgb().string(),
                borderBottomColor: Color(componentColor).alpha(1).rgb().string(),
                backgroundColor: Color(componentColor).alpha(0.06).rgb().string()
            }
            ]}
        >
            {
                !customSelector &&
                <TouchableWithoutFeedback onPress={toggleSelector} disabled={loading}>
                    <View style={[styles.wrapper]}>
                        <Text style={styles.inputTitle}>
                            {translate("Booking Interval")}
                        </Text>
                        <View style={styles.intervalTextContainer}>
                            <Text style={[styles.intervalText, loading && {opacity: 0.5}]}>
                                {chosenSlot ? (formatTimeFromMoment(moment(chosenSlot.start, 'HH:mm')) + ' - ' + formatTimeFromMoment(moment(chosenSlot.end, 'HH:mm'))) : null}
                            </Text>
                            <Text style={styles.durationText}>
                                {chosenSlot ? ("  " + (chosenSlot.end === '00:00' ? moment(chosenSlot.end, 'HH:mm').add(1, 'days') : moment(chosenSlot.end, 'HH:mm')).diff(moment(chosenSlot.start, 'HH:mm'), 'minutes') + " " + translate("minutes")) : null}
                            </Text>
                            <Icon size={actuatedNormalize(18)} name={selectorVisible ? "up" : "down"} type="antdesign" color="#E3E3E3" containerStyle={styles.icon}/>
                        </View>
                    </View>
                </TouchableWithoutFeedback>
            }
            {
                selectorVisible && !customSelector &&
                    <ScrollView contentContainerStyle={[intervals.length < 4 && {flex: 1}]} showsHorizontalScrollIndicator={false} bounces={false} horizontal={true} style={{flex: 0.75, borderTopColor:  Color(componentColor).alpha(0.4).rgb().string(), borderTopWidth: 1}}>
                        {intervals.map(renderInterval)}
                    </ScrollView>
            }
            {
                customSelector ?
                    <View style={styles.customSelector}>
                        <View style={{flex: 0.2, justifyContent: 'center'}}>
                            <Text style={styles.inputTitle}>
                                {translate("Custom interval")}
                            </Text>
                        </View>
                        <View style={[styles.pickerContainer, {flex: 0.25}]}>
                            <Picker name={"start"} time={customStart} minTime={minStartTime} maxTime={(parseInt(daySchedule.end) - 1) + ":55"} onChange={setCustomStart} warning={customInfo}/>
                            <Picker name={"end"} time={customEnd} minTime={moment(customStart, 'HH:mm').add(config.granularity, 'minutes').format('HH:mm')} warning={isIntervalDisabled(date, customStart, customEnd)} maxTime={maxEndTime} onChange={setCustomEnd}/>
                        </View>
                        <View style={{flex: 0.1, justifyContent: 'center'}}>
                            <Text style={styles.customInfo}>
                                {customInfo ? customInfo : (isIntervalDisabled(date, customStart, customEnd) ? translate("This interval is not available") : null)}
                            </Text>
                        </View>
                        <View style={[styles.pickerContainer, {flex: 0.25}]}>
                            <TouchableOpacity disabled={(!!customInfo) || !chosenSlot || isIntervalDisabled(date, customStart, customEnd)} onPress={setCustomSlot} style={[styles.button, {backgroundColor: Color(componentColor).rotate(20).alpha(customInfo || isIntervalDisabled(date, customStart, customEnd) ? 0.3 : 1).rgb().string()}]}>
                                <Text style={[styles.buttonText, customInfo && {opacity: 0.3}]}>
                                    {translate("Confirm").toUpperCase()}
                                </Text>
                            </TouchableOpacity>
                            <TouchableOpacity onPress={() => toggleCustomSelector(false)} style={[styles.button, {backgroundColor: Color(componentColor).rotate(20).rgb().string()}]}>
                                <Text style={styles.buttonText}>
                                    {translate("Cancel").toUpperCase()}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    </View> : null
            }
        </GestureHandlerRootView>
    );
}

import ReactDatePicker from 'react-datepicker'; // For web
import 'react-datepicker/dist/react-datepicker.css';
import {isWEB} from "../services/helpers/getPlatformType";

const Picker = ({ name, time, minTime, maxTime, onChange, warning }) => {
    const config = useSelector(getConfig);
    const [open, setOpen] = useState(false);

    const togglePicker = () => {
        setOpen(true);
    };

    const handleConfirm = (date) => {
        setOpen(false);
        onChange(moment(date).format('HH:mm'));
    };

    return (
        <View style={{ flex: 0.5, justifyContent: 'center' }}>
            <TouchableOpacity onPress={togglePicker}>
                <View>
                    <Text style={styles.inputTitle}>
                        {name.charAt(0).toUpperCase() + name.slice(1)} {translate("time")}
                    </Text>
                    <Text style={[styles.timeText, warning && { color: 'red' }]}>
                        {formatTimeFromMoment(moment(time, 'HH:mm'))}
                    </Text>

                    {/* Conditionally render the picker based on platform */}
                    {isWEB() ? (
                        open && (
                            <ReactDatePicker
                                selected={moment(time, 'HH:mm').toDate()}
                                onChange={(date) => handleConfirm(date)}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={config.granularity}
                                timeCaption="Time"
                                dateFormat="HH:mm"
                                minTime={moment(minTime, 'HH:mm').toDate()}
                                maxTime={moment(maxTime, 'HH:mm').toDate()}
                                onClickOutside={() => setOpen(false)}
                                inline
                            />
                        )
                    ) : (
                        <DateTimePickerModal
                            isVisible={open}
                            mode="time"
                            date={moment(time, 'HH:mm').toDate()}
                            onCancel={() => setOpen(false)}
                            onConfirm={handleConfirm}
                            locale={config.time_format === 'ampm' ? 'en' : 'fr'}
                            is24Hour
                            minimumDate={moment(minTime, 'HH:mm').toDate()}
                            maximumDate={moment(maxTime, 'HH:mm').toDate()}
                            minuteInterval={config.granularity}
                        />
                    )}
                </View>
            </TouchableOpacity>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 0.25,
        justifyContent: 'center',
        borderWidth: 1,
        borderRadius: 5,
        borderBottomWidth: 2,
    },
    containerWithSelector: {
        flex: 0.50,
        justifyContent: 'space-between',
        // borderBottomWidth: 1,
    },
    wrapper: {
        justifyContent: 'center',
        paddingLeft: '7.5%',
        paddingRight: '7.5%',
        flex: 1.5
    },
    inputTitle: {
        fontFamily: FONT_FAMILY_LIGHT,
        fontSize: actuatedNormalize(18),
        color: '#FFFFFF'
    },
    timeText: {
        fontSize: actuatedNormalize(18),
        fontFamily: FONT_FAMILY_LIGHT,
        color: '#93A4AB',
        marginTop: '5%'
    },
    intervalTextContainer: {
        flexDirection: 'row'
    },
    intervalText: {
        color: '#FFFFFF',
        textAlign: 'left',
        fontFamily: FONT_FAMILY,
        fontSize: actuatedNormalize(18)
    },
    durationText: {
        color: '#749097',
        fontFamily: FONT_FAMILY_LIGHT,
        fontSize: actuatedNormalize(18),
        opacity: 0.8
    },
    icon: {
        position: 'absolute',
        right: 0
    },
    intervalContainer: {
        flex: 1,
        width: actuatedNormalize(125),
        justifyContent: 'center',
        alignItems: 'center',
        // paddingTop: '3%',
        // paddingBottom: '3%',
        borderRightWidth: 1,
        borderLeftWidth: 1,
    },
    intervalSelected: {
        // backgroundColor: 'green'
    },
    intervalName: {
        color: '#FFFFFF',
        textTransform: 'uppercase',
        fontFamily: FONT_FAMILY_BOLDER,
        fontSize: actuatedNormalize(12),
        paddingLeft: '10%',
        paddingRight: '10%',
    },
    intervalDuration: {
        fontFamily: FONT_FAMILY_LIGHT,
        color: '#FFFFFF',
        fontSize: actuatedNormalize(12)
    },
    button: {
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 2.5,
        marginRight: '15%',
        paddingRight: actuatedNormalize(32),
        paddingLeft: actuatedNormalize(32),
        height: '70%'
    },
    buttonText: {
        color: 'white',
        fontFamily:FONT_FAMILY,
        fontSize: actuatedNormalize(12),
        textAlign: 'center',
    },
    hour: {
        fontSize: actuatedNormalize(18),
        fontFamily: FONT_FAMILY_LIGHT,
        color: '#93A4AB',
        marginTop: '5%'
    },
    customSelector: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        justifyContent: 'space-around',
        paddingLeft: '7.5%',
        paddingRight: '7.5%',
    },
    pickerContainer: {
        flex: 0.3,
        flexDirection: 'row',
        alignItems: 'center'
    },
    customInfo: {
        fontSize: actuatedNormalize(10),
        color: "red"
    }
});

export default BookingIntervalContainer;
